import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { AdditionalInterestType } from "@covertree/new-business-graphql";

import { getInterestTypeOptions } from "./consts";
import {
  AdditionalInterestFormData,
  AdditionalInterestFormMode,
  AdditionalInterestSource,
  defaultAdditionalInterestFormData,
  normalizeZipCode,
  toInterestFormData,
  toInterestFormValues,
} from "./formData";
import {
  AdditionalInterestFormValues,
  useLenderAutofill,
} from "./lenderAutofill";
import { createAdditionalInterestValidator } from "./validator";

interface Params {
  readonly mode: AdditionalInterestFormMode;
  readonly onSubmit: (data: AdditionalInterestFormData) => void;
  readonly onClose: () => void;
  readonly interestsCountByType: Record<AdditionalInterestType, number>;
  readonly selectedInterest?: AdditionalInterestSource | null;
  /** Per-type caps. Checkout varies them by payment plan. */
  readonly maxInterests?: Record<AdditionalInterestType, number>;
  readonly isTenantPolicy?: boolean;
  readonly isSubmitting?: boolean;
  /** Modals stay mounted while closed, so they re-seed the form on open. */
  readonly isOpen?: boolean;
}

export const useAdditionalInterestForm = ({
  mode,
  onSubmit,
  onClose,
  interestsCountByType,
  selectedInterest = null,
  maxInterests,
  isTenantPolicy = false,
  isSubmitting = false,
  isOpen = true,
}: Params) => {
  const selectedInterestType = selectedInterest?.type
    ? (selectedInterest.type as AdditionalInterestType)
    : undefined;

  const form = useForm<AdditionalInterestFormValues>({
    defaultValues: toInterestFormValues(
      selectedInterest ?? defaultAdditionalInterestFormData,
      isTenantPolicy
    ),
    mode: "all",
    resolver: yupResolver(
      createAdditionalInterestValidator(
        interestsCountByType,
        selectedInterestType,
        maxInterests
      )
    ) as Resolver<AdditionalInterestFormValues>,
  });

  const {
    control,
    formState: { isValid, isDirty },
    reset,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    watch,
  } = form;

  const selectedType = useWatch({ control, name: "type" });
  const lenderOption = watch("name");

  const typeOptions = useMemo(
    () => getInterestTypeOptions(isTenantPolicy),
    [isTenantPolicy]
  );

  const { skipLenderAutofillRef } = useLenderAutofill({
    lenderOption: isTenantPolicy ? null : lenderOption,
    setValue,
    getValues,
    skipInitialAutofill: Boolean(selectedInterest?.name),
  });

  // Runs after the lender autofill above, which copies the zip as stored in the
  // lender list; bring it to the digits the zip field formats.
  useEffect(() => {
    const zipCode = getValues("zipCode");
    const normalized = normalizeZipCode(zipCode);

    if (normalized !== zipCode) {
      setValue("zipCode", normalized, { shouldValidate: true });
    }
  }, [lenderOption, getValues, setValue]);

  // Re-checks the per-type cap, which depends on the type that is picked.
  useEffect(() => {
    if (selectedType) {
      trigger("type");
    }
  }, [selectedType, trigger]);

  const isSeededRef = useRef(false);

  // Seed once per opening. The inline form is long-lived, so re-seeding on
  // every new selectedInterest object would wipe what the user is typing.
  useEffect(() => {
    if (!isOpen) {
      isSeededRef.current = false;
      return;
    }

    if (isSeededRef.current) return;
    isSeededRef.current = true;

    // An edited interest arrives pre-filled: keep the autofill from
    // overwriting the saved name, and validate the seeded values up front.
    skipLenderAutofillRef.current = Boolean(selectedInterest?.name);
    reset(
      toInterestFormValues(
        selectedInterest ?? defaultAdditionalInterestFormData,
        isTenantPolicy
      )
    );

    if (selectedInterest) {
      trigger();
    }
  }, [isOpen, selectedInterest, isTenantPolicy, reset, trigger]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;

    skipLenderAutofillRef.current = false;
    reset(
      toInterestFormValues(defaultAdditionalInterestFormData, isTenantPolicy)
    );
    onClose();
  }, [onClose, reset, isTenantPolicy, isSubmitting, skipLenderAutofillRef]);

  const canSubmit = isValid && !isSubmitting && (mode === "add" || isDirty);

  const submitForm = handleSubmit((values) => {
    // Nothing to save when an edited interest was left untouched.
    if (mode !== "add" && !isDirty) return;

    onSubmit(toInterestFormData(values));
  });

  return {
    form,
    mode,
    typeOptions,
    canSubmit,
    isTenantPolicy,
    isSubmitting,
    onSubmit: submitForm,
    onCancel: handleClose,
  };
};
